import axios from 'axios'
import { passThroughAxios } from './passThrough.axios'
import { normalizeQuote, denormalizeQuoteDetails, withErrorMessages } from './quote.normalize'
import { EventBus } from '@/plugins/event-bus'

const CancelToken = axios.CancelToken
let previewSaveCancelFunction
let updateCancelFunction

// passThroughAxios's error interceptor only shows a `Messages` array, which C# sent; PCQM sends a
// single `Message`, so it is shown here, where it becomes `Messages` for the callers (CST-4514).
// A body that already had `Messages` was shown by the interceptor and is not shown twice.
function toQuoteError(e) {
  const data = e?.response?.data
  const shownByInterceptor = Array.isArray(data?.Messages) || Array.isArray(data?.messages)
  const error = withErrorMessages(e)
  if (!shownByInterceptor) {
    error?.response?.data?.Messages?.forEach((message) =>
      EventBus.$emit('notification-warning', message)
    )
  }
  return error
}

function setEstablishmentIdFromUrl(quoteDetails) {
  const establishmentId = getEstablishmentIdFromUrl()
  if (establishmentId === null) return quoteDetails

  if (quoteDetails?.Dealer) quoteDetails.Dealer.EstablishmentID = establishmentId
  else if (quoteDetails) quoteDetails.Dealer = { EstablishmentID: establishmentId }
  else quoteDetails = { Dealer: { EstablishmentID: establishmentId } }

  return quoteDetails
}

// The employee travels in the body because PCQM only sees a client-credentials token and cannot
// look the signed-in user up (CST-4514). Everything else C# filled in for a new quote -- the dealer's
// establishment details, the dates, the customer PoId "0" -- is done by PCQM's CST routes
// (CstQuoteWriteService.applyDefaults), so no client has to know those data rules.
async function postNewQuote(url, quoteDetails, employee, config) {
  if (!quoteDetails?.Dealer?.EstablishmentID) {
    quoteDetails = setEstablishmentIdFromUrl(quoteDetails)
  }
  const body = denormalizeQuoteDetails({
    ...quoteDetails,
    Employee: employee ?? quoteDetails?.Employee,
  })
  const response = await passThroughAxios
    .post(url, body, config)
    .catch((e) => Promise.reject(toQuoteError(e)))
  return { ...response, data: normalizeQuote(response.data) }
}

function getEstablishmentIdFromUrl() {
  const urlParts = window.location.pathname.split('/')
  if (urlParts.length > 3) return urlParts[3]
  return null
}

export default {
  async getQuoteById(quoteId) {
    try {
      const response = await passThroughAxios.get(`quotes/${quoteId}`)
      return normalizeQuote(response.data)
    } catch (e) {
      return Promise.reject(toQuoteError(e))
    }
  },

  async previewSave(quote) {
    if (quote.QuoteDetails.ExchangeCars) {
      for (let exchangeCar of quote.QuoteDetails.ExchangeCars) {
        if (exchangeCar && exchangeCar.Price) {
          exchangeCar.Price.BtwRate = 21 // this is hardcoded for now, comes from PCCM in the future
        }
      }
    }

    //cancel previous call
    previewSaveCancelFunction && previewSaveCancelFunction()

    if (!quote.QuoteDetails?.Dealer?.EstablishmentID) {
      quote.QuoteDetails = setEstablishmentIdFromUrl(quote.QuoteDetails)
    }

    if (quote.QuoteId) {
      try {
        const response = await passThroughAxios.post(
          `quotes/${quote.QuoteId}/previewsave`,
          denormalizeQuoteDetails(quote.QuoteDetails),
          {
            cancelToken: new CancelToken((c) => {
              previewSaveCancelFunction = c
            }),
          }
        )

        return normalizeQuote(response.data)
      } catch (e) {
        return Promise.reject(toQuoteError(e).response?.data)
      }
    } else {
      return Promise.reject()
    }
  },

  // PCQM copies Employee and Dealer from the body, so they must be right in the store: the
  // "take over" flow puts the employee there, and "transfer establishment" puts the establishment
  // there without a DealerID, which PCQM fills in from the establishment. C# used to swap in the
  // stored quote's values unless updateEmployee/updateEstablishment was set; those flags are no
  // longer needed. `options` is kept for the callers' signature (CST-4514).
  async update(quote, options) {
    let quoteDetails = quote.QuoteDetails

    if (!quoteDetails?.Dealer?.EstablishmentID) {
      quoteDetails = setEstablishmentIdFromUrl(quoteDetails)
    }

    //cancel previous call
    updateCancelFunction && updateCancelFunction()

    const response = await passThroughAxios
      .put(`quotes/${quote.QuoteId}`, denormalizeQuoteDetails(quoteDetails), {
        cancelToken: new CancelToken((c) => {
          updateCancelFunction = c
        }),
      })
      .catch((e) => Promise.reject(toQuoteError(e)))

    return { ...response, data: normalizeQuote(response.data) }
  },
  duplicate(quoteIdToDuplicate, employee) {
    return passThroughAxios.post(`quotes/${quoteIdToDuplicate}/duplicate`, employee)
  },
  create(quoteDetails, employee) {
    return postNewQuote('quotes', quoteDetails, employee)
  },

  createFromCartId(quoteDetails, cartId, employee) {
    return postNewQuote('carts/quotes', quoteDetails, employee, { params: { cartId } })
  },

  // PCQM stamps the new quote with whoever created it and only ever sees a
  // client-credentials token, so the employee travels in the body -- same as duplicate().
  async createForNewStockCar(quoteDetails, commissionNumber, commissionYear, employee) {
    if (!quoteDetails?.Dealer?.EstablishmentID) {
      quoteDetails = setEstablishmentIdFromUrl(quoteDetails)
    }
    const body = denormalizeQuoteDetails({ ...quoteDetails, Employee: employee })
    const response = await passThroughAxios.post('stockcarsNew/createQuote', body, {
      params: { commissionNumber, commissionYear: commissionYear ?? '' },
    })
    return { ...response, data: normalizeQuote(response.data) }
  },

  // C# only converted the body here: the child copies the parent's details, employee included.
  async createChildQuote(parentQuoteId, quoteDetails) {
    const response = await passThroughAxios
      .post(`quotefrom/${parentQuoteId}`, denormalizeQuoteDetails(quoteDetails))
      .catch((e) => Promise.reject(toQuoteError(e)))
    return { ...response, data: normalizeQuote(response.data) }
  },

  requestQuote(quoteId, shouldSendmail, shouldCCEmployee, reserveInIds = false) {
    return axios.post(
      `RequestQuote/${quoteId}?sendEmailToCustomer=${shouldSendmail}&sendEmailToEmployee=${shouldCCEmployee}&reserveCar=${reserveInIds}`
    )
  },

  requestSalesOrder(salesOrderParams) {
    let {
      quoteId,
      sendEmailToCustomer,
      sendCopyToEmployee,
      sendSigning = false,
      digitalSigningParams = null,
      reserveInIds = false,
      isApprovalRequest,
    } = salesOrderParams
    return axios.post(
      `RequestSalesOrder/${quoteId}?sendEmailToCustomer=${sendEmailToCustomer}&sendEmailToEmployee=${sendCopyToEmployee}&reserveCar=${reserveInIds}&sendSigning=${sendSigning}&isApprovalRequest=${isApprovalRequest}`,
      digitalSigningParams,
      {
        headers: {
          // Overwrite Axios's automatically set Content-Type
          // it sets incorrect content type when digitalSigningParams is null
          'Content-Type': 'application/json',
        },
      }
    )
  },

  // `ponId` here is the overview's employee filter, not the caller -- it goes out as employeePonId.
  // The sort order and the page/size defaults are what the retired C# action always sent.
  async getQuotes(establishmentId, page, amount, ponId, status, search) {
    const params = {
      establishment: establishmentId,
      page: page ?? 1,
      size: amount ?? 50,
      employeePonId: ponId,
      status,
      search,
      orderby: 'lastupdatedon',
      ordertype: 'desc',
    }
    try {
      const response = await passThroughAxios.get('quotes', { params })
      return (response.data?.Results ?? []).map(normalizeQuote)
    } catch (e) {
      return Promise.reject(toQuoteError(e))
    }
  },

  // Archives the quote (PCQM closes it). Via C# no ponId was sent, which blanked the quote's
  // LastUpdatedBy; the pass-through records the signed-in user (CST-4514).
  async delete(quoteId) {
    try {
      const response = await passThroughAxios.delete(`quotes/${quoteId}`)
      return response.data
    } catch (e) {
      return Promise.reject(toQuoteError(e))
    }
  },
  // PCQM records the signed-in user (session ponId header) as LastUpdatedBy. C# looked up the
  // quote owner instead; the user chose the actual user (CST-4514 step 4) -- see the progress doc
  // for why this does not change who handles digital signing.
  async changeStatus(quoteId, newStatus) {
    try {
      const response = await passThroughAxios.put(`quotes/${quoteId}/changestatus`, null, {
        params: { status: newStatus },
      })
      return normalizeQuote(response.data)
    } catch (e) {
      return Promise.reject(toQuoteError(e))
    }
  },
  // An empty newStatus (EditSalesOrder resets it with '') is sent as `status=`; PCQM treats a
  // blank as "no signing status", as it did via C#.
  async changeSigningStatus(quoteId, newStatus) {
    try {
      const response = await passThroughAxios.put(`quotes/${quoteId}/changesigningstatus`, null, {
        params: { status: newStatus },
      })
      return normalizeQuote(response.data)
    } catch (e) {
      return Promise.reject(toQuoteError(e))
    }
  },
  async updateCustomerType(quoteId, customerTypeId) {
    try {
      const response = await passThroughAxios.put(
        `quotes/${quoteId}/customertype/${customerTypeId}`
      )
      return response.data
    } catch (e) {
      return Promise.reject(toQuoteError(e))
    }
  },
  async getQuoteEmailInfoForDigitalSigning(quoteId) {
    try {
      const response = await passThroughAxios.get(`quotes/${quoteId}/email/info/digital-signing`)
      return Promise.resolve(response.data)
    } catch (e) {
      return Promise.reject(e)
    }
  },
}
